/**
 * Pago a proveedor en Dólares (Remito y Adelanto): la tasa se carga por línea, como en Ventas.
 * Se envía `monto` (ARS = USD × tasa), `montoUsd` y `cotizacion`; el backend usa `monto_usd`
 * para el disponible USD del cierre y el Haber USD.
 */
import { roundToPeso } from "@/lib/roundToPeso";
import { roundMoney } from "@/lib/utils";

export function normalizarTasa(raw: unknown): number {
  const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? "").replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100) / 100;
}

/** ARS equivalente de una línea en USD; 0 si falta importe o tasa. */
export function arsDeLineaUsd(importeUsd: number, tasa: unknown): number {
  const t = normalizarTasa(tasa);
  if (!(importeUsd > 0) || t <= 0) return 0;
  return roundToPeso(importeUsd * t);
}

/** USD sugerido para cubrir el restante en ARS con la tasa cargada. */
export function usdSugeridoParaRestante(restanteArs: number, tasa: unknown): number {
  const t = normalizarTasa(tasa);
  if (!(restanteArs > 0) || t <= 0) return 0;
  return roundToPeso(restanteArs / t);
}

/**
 * USD a precargar al cargar/cambiar la tasa en un pago tipo Remito:
 * round(restante ARS del remito / tasa, 2). null = no tocar el importe (Adelanto, sin tasa o sin restante).
 */
export function usdAutoPorTasaRemito(
  pagoTipo: "remito" | "adelanto",
  restanteRemitoArs: number,
  tasa: unknown,
): number | null {
  if (pagoTipo !== "remito") return null;
  const t = normalizarTasa(tasa);
  if (!(restanteRemitoArs > 0) || t <= 0) return null;
  return roundMoney(restanteRemitoArs / t);
}

export function metodoPagoUsdPayload(importeUsd: number, tasa: unknown): {
  monto: number;
  montoUsd: number;
  cotizacion: number;
} {
  const cotizacion = normalizarTasa(tasa);
  const montoUsd = roundMoney(importeUsd);
  return { monto: arsDeLineaUsd(montoUsd, cotizacion), montoUsd, cotizacion };
}
